import { useEffect, useRef } from 'react';
import { useAuth } from '../../user-dashboard/auth/AuthContext';

const POLL_MS = 120000;

/**
 * Blocks the dashboard / map for guest users when the IP-based compute budget is 0.
 * Only option is to log in (clears guest session and opens the auth experience).
 * Infrequent /auth/me refresh corrects drift; metered tools also update the budget locally via
 * the inference response header (see inferenceComputeBudget).
 */
export default function GuestTimeUpOverlay() {
  const { user, refreshUser, logout, loading: authLoading } = useAuth();
  const guest = user?.is_system_guest === true;
  const rem = user?.guest_quota?.remaining_ms;
  const isExhausted = guest && typeof rem === 'number' && rem <= 0;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!guest || authLoading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      refreshUser().catch(() => undefined);
    }, POLL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [guest, authLoading, refreshUser]);

  if (authLoading || !isExhausted) return null;

  const onLogin = () => {
    logout();
  };

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 pointer-events-auto">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 p-6 sm:p-8 text-center">
        <h2 className="text-xl font-bold text-gray-900">Guest time is up</h2>
        <p className="text-sm text-gray-600 mt-2">
          The free guest session for this network has ended. Sign in to keep working with your own
          compute budget.
        </p>
        <button
          type="button"
          onClick={onLogin}
          className="mt-5 w-full py-3 px-4 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition"
        >
          Log in
        </button>
        <p className="text-xs text-gray-500 mt-3">You’ll be taken to the sign-in page. Create an account if you don’t have one.</p>
      </div>
    </div>
  );
}
